import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  PresentationChartLineIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Bar, Line
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DemandForecastAnalysis = () => {
  const navigate = useNavigate();
  const { products, sales, customers } = useAppData();
  const { companySettings } = useCompanySettings();
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [timeRange, setTimeRange] = useState('6months');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  // State for UI indicators and data
  const [forecastData, setForecastData] = useState([]);
  const [productStats, setProductStats] = useState({});
  
  // State for internal processing
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productForecasts, setProductForecasts] = useState([]);
  
  // Périodes disponibles
  const timeRanges = [
    { id: '3months', name: '3 mois' },
    { id: '6months', name: '6 mois' },
    { id: '12months', name: '1 an' }
  ];

  // Extraire les catégories réelles des produits
  const [categories, setCategories] = useState([
    { id: 'all', name: 'Toutes les catégories' }
  ]);
  
  // Mettre à jour les catégories basées sur les produits réels
  useEffect(() => {
    if (products && products.length > 0) {
      const productCategories = [...new Set(products.map(p => p.category))].filter(Boolean);
      
      if (productCategories.length > 0) {
        setCategories([
          { id: 'all', name: 'Toutes les catégories' },
          ...productCategories.map(cat => ({ id: cat, name: cat }))
        ]);
      }
    }
  }, [products]);
  
  // Filtrer les produits selon la catégorie
  const filteredProducts = categoryFilter === 'all' 
    ? (products.length > 0 ? products : []) 
    : (products.length > 0 ? products.filter(product => product.category === categoryFilter) : []);

  // Ajouter l'option "Tous les produits" à la liste
  const productOptions = [
    { id: 'all', name: 'Tous les produits' },
    ...filteredProducts.map(product => ({
      id: product.id,
      name: product.name
    }))
  ];

  // Import transformSalesData if needed
  const transformRealSalesData = useMemo(() => {
    if (!sales || !sales.length) return [];
    
    // Use the shared transformation utility for consistency
    try {
      const { transformSalesData } = require('../../utils/salesDataUtils');
      return transformSalesData(sales, customers);
    } catch (error) {
      console.error("Error transforming sales data:", error);
      return sales;
    }
  }, [sales, customers]);
  
  // Calculer les prévisions avec useCallback pour éviter les dépendances circulaires
  const calculateForecast = useCallback(() => {
    setIsCalculating(true);
    
    try {
      console.log('🔄 Calculating demand forecast with real-time data');
      console.log('📊 Data check:', {
        productsCount: products?.length || 0,
        salesCount: transformRealSalesData?.length || 0,
        customersCount: customers?.length || 0
      });
      
      // Générer les mois historiques et prévisionnels
      const numberOfMonths = timeRange === '3months' ? 3 : timeRange === '6months' ? 6 : 12;
      const currentDate = new Date();
      const forecastMonths = [];
      
      // Générer les mois historiques et prévisionnels
      for (let i = -12; i < numberOfMonths; i++) {
        const date = new Date();
        date.setMonth(currentDate.getMonth() + i);
        const monthName = date.toLocaleDateString('fr-FR', { month: 'short' });
        const year = date.getFullYear();
        const label = `${monthName} ${year}`;
        
        forecastMonths.push({
          month: label,
          isHistory: i < 0,
          isForecast: i >= 0,
          date: date,
          index: i,
          monthYear: `${date.getMonth()}-${date.getFullYear()}` // For grouping
        });
      }
      
      // Extraire les données de ventes réelles par produit et par mois
      const realSalesByProduct = {};
      
      if (transformRealSalesData && transformRealSalesData.length > 0) {
        transformRealSalesData.forEach(sale => {
          if (!sale.items || !Array.isArray(sale.items)) return;
          
          const saleDate = new Date(sale.rawDate);
          const monthYear = `${saleDate.getMonth()}-${saleDate.getFullYear()}`;
          
          sale.items.forEach(item => {
            const productId = item.productId || item.id;
            if (!productId) return;
            
            const quantity = parseFloat(item.quantity) || 0;
            if (quantity <= 0) return;
            
            if (!realSalesByProduct[productId]) {
              realSalesByProduct[productId] = {};
            }
            
            if (!realSalesByProduct[productId][monthYear]) {
              realSalesByProduct[productId][monthYear] = 0;
            }
            
            realSalesByProduct[productId][monthYear] += quantity;
          });
        });
      }
      
      console.log('📈 Real sales data extracted:', Object.keys(realSalesByProduct).length, 'products with sales history');
      
      // Générer des données de demande historique et prévisionnelle
      const productForecasts = filteredProducts.map(product => {
        // Utiliser les données réelles du produit
        const stock = parseInt(product.stock) || 1;
        const price = parseFloat(product.price) || 10;
        const costPrice = parseFloat(product.costPrice || product.cost) || price * 0.6;
        
        // Facteurs saisonniers basés sur les mois de l'année
        const seasonality = [1.1, 0.9, 0.85, 1.0, 1.05, 1.15, 1.2, 1.1, 0.95, 0.9, 0.95, 1.05];
        
        // Récupérer l'historique des ventes réelles pour ce produit
        const productSalesHistory = realSalesByProduct[product.id] || {};
        
        // Calculer la tendance à partir des données réelles
        let realHistoricalSales = [];
        let realTotalSales = 0;
        let realMonthsWithSales = 0;
        
        // Collecter les données historiques réelles
        forecastMonths.filter(m => m.isHistory).forEach(month => {
          const monthYear = month.monthYear;
          const realSales = productSalesHistory[monthYear] || 0;
          
          if (realSales > 0) {
            realMonthsWithSales++;
            realTotalSales += realSales;
          }
          
          realHistoricalSales.push({
            month: month.month,
            sales: realSales,
            monthIndex: month.date.getMonth()
          });
        });
        
        // Calculer la moyenne des ventes mensuelles
        const avgMonthlySales = realMonthsWithSales > 0 ? realTotalSales / realMonthsWithSales : 0;
        
        // Calculer la tendance (croissance ou décroissance)
        let trend = 0;
        if (realHistoricalSales.length >= 2 && realMonthsWithSales >= 2) {
          // Calculer la tendance linéaire simple
          const recentMonths = realHistoricalSales.slice(-6); // Derniers 6 mois
          const monthsWithSales = recentMonths.filter(m => m.sales > 0);
          
          if (monthsWithSales.length >= 2) {
            const firstMonth = monthsWithSales[0];
            const lastMonth = monthsWithSales[monthsWithSales.length - 1];
            const monthsDiff = monthsWithSales.length - 1;
            
            if (monthsDiff > 0 && firstMonth.sales > 0) {
              trend = ((lastMonth.sales - firstMonth.sales) / firstMonth.sales) * 100 / monthsDiff;
            }
          }
        }
        
        // Si pas assez de données réelles, estimer la tendance
        if (realMonthsWithSales < 2) {
          // Utiliser le prix et le stock pour estimer une tendance
          const priceInfluence = Math.min(price / 1000, 1) * 10; // Influence du prix sur la tendance
          const stockInfluence = Math.min(stock / 100, 1) * 5; // Influence du stock sur la tendance
          const marginInfluence = price > costPrice ? ((price - costPrice) / price) * 5 : 0;
          
          trend = stockInfluence - priceInfluence + marginInfluence;
        }
        
        // Limiter la tendance à des valeurs raisonnables
        trend = Math.max(Math.min(trend, 30), -20);
        
        // Volume de base pour les prévisions
        const baseVolume = Math.max(avgMonthlySales, stock * 0.1);
        
        const monthlyData = forecastMonths.map((month) => {
          // Calculer l'effet saisonnier
          const monthIndex = month.date.getMonth();
          const seasonFactor = seasonality[monthIndex];
          
          // Calculer la demande
          let demand;
          
          if (month.isHistory) {
            // Utiliser les données historiques réelles si disponibles
            const monthYear = month.monthYear;
            const realSales = productSalesHistory[monthYear] || 0;
            
            if (realSales > 0) {
              // Données réelles
              demand = realSales;
            } else {
              // Estimation pour les mois sans données
              demand = Math.max(0, Math.round(baseVolume * seasonFactor));
            }
          } else {
            // Données prévisionnelles basées sur la tendance et la saisonnalité
            const monthsInFuture = month.index;
            const trendFactor = 1 + (trend / 100 * monthsInFuture);
            const forecastValue = Math.max(0, Math.round(baseVolume * seasonFactor * trendFactor));
            
            // Ajouter des intervalles de confiance pour les prévisions
            const confidenceInterval = 0.1 * Math.sqrt(monthsInFuture + 1); // Incertitude croissante
            demand = {
              expected: forecastValue,
              lower: Math.round(forecastValue * (1 - confidenceInterval)),
              upper: Math.round(forecastValue * (1 + confidenceInterval))
            };
          }
          
          return {
            month: month.month,
            isHistory: month.isHistory,
            isForecast: month.isForecast,
            demand: demand
          };
        });
        
        // Calculer les statistiques
        const historicalData = monthlyData.filter(m => m.isHistory);
        const avgHistorical = historicalData.reduce((sum, m) => sum + m.demand, 0) / historicalData.length;
        const maxHistorical = Math.max(...historicalData.map(m => m.demand));
        const forecastData = monthlyData.filter(m => m.isForecast);
        const avgForecast = forecastData.reduce((sum, m) => sum + m.demand.expected, 0) / forecastData.length;
        const growthRate = avgHistorical > 0 ? ((avgForecast - avgHistorical) / avgHistorical * 100) : 0;
        
        // Calculer la fiabilité en fonction de la qualité des données
        const reliability = Math.min(95, Math.max(50, 
          75 + // Base reliability
          (realMonthsWithSales * 2) - // Plus de mois avec des données réelles = plus fiable
          Math.abs(trend) * 0.5 - // Tendance extrême = moins fiable
          (avgMonthlySales === 0 ? 20 : 0) // Pas de ventes historiques = moins fiable
        ));
        
        return {
          id: product.id,
          name: product.name,
          category: product.category || 'Non catégorisé',
          monthlyData,
          stats: {
            avgHistorical: Math.round(avgHistorical),
            maxHistorical,
            avgForecast: Math.round(avgForecast),
            growthRate: parseFloat(growthRate.toFixed(1)),
            trend: parseFloat(trend.toFixed(2)),
            reliability: Math.round(reliability),
            realMonthsWithSales
          }
        };
      });
      
      // Store the product forecasts for the product table
      setProductForecasts(productForecasts);
      
      // Gérer le filtre de produit spécifique
      if (productFilter !== 'all') {
        const selectedProd = productForecasts.find(p => p.id === productFilter);
        if (selectedProd) {
          setSelectedProduct(selectedProd);
          setProductStats(selectedProd.stats);
          setForecastData(selectedProd.monthlyData);
        }
      } else {
        setSelectedProduct(null);
        
        // Créer des données agrégées pour tous les produits
        const aggregatedData = forecastMonths.map(month => {
          const monthLabel = month.month;
          const allProductsForMonth = productForecasts.map(product => {
            const monthData = product.monthlyData.find(m => m.month === monthLabel);
            return monthData;
          });
          
          if (month.isHistory) {
            // Pour les données historiques
            const totalDemand = allProductsForMonth.reduce((sum, m) => sum + (m ? m.demand : 0), 0);
            return { month: monthLabel, isHistory: true, isForecast: false, demand: totalDemand };
          } else {
            // Pour les prévisions
            const expected = allProductsForMonth.reduce((sum, m) => sum + (m && m.demand ? m.demand.expected : 0), 0);
            const lower = allProductsForMonth.reduce((sum, m) => sum + (m && m.demand ? m.demand.lower : 0), 0);
            const upper = allProductsForMonth.reduce((sum, m) => sum + (m && m.demand ? m.demand.upper : 0), 0);
            
            return {
              month: monthLabel,
              isHistory: false,
              isForecast: true,
              demand: { expected, lower, upper }
            };
          }
        });
        
        // Calculer les statistiques globales
        const historicalData = aggregatedData.filter(m => m.isHistory);
        const avgHistorical = historicalData.reduce((sum, m) => sum + m.demand, 0) / historicalData.length;
        const maxHistorical = Math.max(...historicalData.map(m => m.demand));
        const forecastData = aggregatedData.filter(m => m.isForecast);
        const avgForecast = forecastData.reduce((sum, m) => sum + m.demand.expected, 0) / forecastData.length;
        const growthRate = avgHistorical > 0 ? ((avgForecast - avgHistorical) / avgHistorical * 100) : 0;
        
        // Calculer la fiabilité moyenne
        const avgReliability = productForecasts.reduce((sum, p) => sum + p.stats.reliability, 0) / productForecasts.length;
        
        setProductStats({
          avgHistorical: Math.round(avgHistorical),
          maxHistorical,
          avgForecast: Math.round(avgForecast),
          growthRate: parseFloat(growthRate.toFixed(1)),
          reliability: Math.round(avgReliability)
        });
        
        setForecastData(aggregatedData);
      }
      
    } catch (error) {
      console.error("Erreur lors du calcul des prévisions:", error);
      toast.error("Erreur lors du calcul des prévisions");
    } finally {
      setIsCalculating(false);
    }
  }, [categoryFilter, productFilter, timeRange, transformRealSalesData, products, filteredProducts, customers?.length]);
  
  // Trigger calculation when dependencies change
  useEffect(() => {
    calculateForecast();
  }, [calculateForecast]);

  // Format pour le graphique - Transformer les données pour l'affichage
  const chartData = forecastData.map(item => {
    if (item.isHistory) {
      return {
        month: item.month,
        "Historique": item.demand,
        "Prévision": null,
        "Min": null,
        "Max": null
      };
    } else {
      return {
        month: item.month,
        "Historique": null,
        "Prévision": item.demand.expected,
        "Min": item.demand.lower,
        "Max": item.demand.upper
      };
    }
  });
  
  // Sélectionner un produit spécifique
  const handleProductSelect = (productId) => {
    setProductFilter(productId);
  };
  
  // PDF Report Generation
  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      console.log('🔄 Starting Demand Forecast PDF generation...');
      console.log('📊 Data check:', {
        productsCount: products?.length || 0,
        salesCount: sales?.length || 0,
        forecastDataLength: forecastData?.length || 0,
        productForecastsLength: productForecasts?.length || 0
      });
      
      // Minimalist red theme colors (consistent with other reports)
      const primaryRed = [185, 28, 28]; // #B91C1C - True red color
      const secondaryRed = [153, 27, 27]; // #991B1B - Darker red
      const lightGray = [248, 248, 248]; // Very light gray for subtle backgrounds
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentPage = 1;
      
      // Helper function to add minimal header and footer
      const addHeaderFooter = async () => {
        // HEADER - Company logo and name
        let headerYPosition = 10;
        
        // Add company logo if available
        if (companySettings?.logo) {
          try {
            // Add logo to PDF (small size: 15mm width, proportional height)
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            
            // Position company name next to logo
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            
            headerYPosition = 18; // Adjust for logo height
          } catch (error) {
            console.log('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 10);
          }
        } else {
          // No logo - just company name
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Black color
          const companyName = companySettings?.companyName || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 10);
        }
        
        // Simple thin line below header
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`${currentPage}`, pageWidth - 25, pageHeight - 8);
      };
      
      // Helper function to add minimal section header
      const addSectionHeader = async (title, yPos) => {
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          currentPage++;
          await addHeaderFooter();
          yPos = 30; // Adjust for new header size
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(title, 20, yPos);
        
        // Thin underline
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.1);
        const textWidth = pdf.getTextWidth(title);
        pdf.line(20, yPos + 2, 20 + textWidth, yPos + 2);
        
        return yPos + 8; // Return new position after header
      };
      
      // Start first page
      await addHeaderFooter();
      let yPosition = 30; // Start below larger header with logo
      
      // Main Title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('PRÉVISIONS DE DEMANDE', 20, yPosition);
      yPosition += 8;
      
      // Date and time
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 20, yPosition);
      yPosition += 20;
      
      // Filters summary
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(60, 60, 60);
      pdf.text('Filtres appliqués:', 20, yPosition);
      yPosition += 5;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      
      // Get the filter names for the report
      const timeRangeText = timeRange === '3months' ? '3 mois' : timeRange === '6months' ? '6 mois' : '1 an';
      const categoryText = categoryFilter === 'all' ? 'Toutes les catégories' : categoryFilter;
      const productText = productFilter === 'all' ? 'Tous les produits' : 
        products.find(p => p.id === productFilter)?.name || 'Produit spécifique';
      
      pdf.text(`Horizon: ${timeRangeText}   |   Catégorie: ${categoryText}   |   Produit: ${productText}`, 20, yPosition);
      yPosition += 15;
      
      // Executive Summary
      yPosition = await addSectionHeader('RÉSUMÉ DES PRÉVISIONS', yPosition);
      
      const summaryData = [
        ['Demande mensuelle moyenne (historique)', `${productStats.avgHistorical || 0}`],
        ['Demande future moyenne (prévisionnelle)', `${productStats.avgForecast || 0}`],
        ['Taux de croissance', `${productStats.growthRate > 0 ? '+' : ''}${productStats.growthRate || 0}%`],
        ['Fiabilité de la prévision', `${productStats.reliability || 0}%`]
      ];
      
      autoTable(pdf, {
        startY: yPosition,
        head: [['Métrique', 'Valeur']],
        body: summaryData,
        theme: 'plain',
        headStyles: { 
          fillColor: primaryRed, 
          textColor: 255, 
          fontSize: 9, 
          halign: 'left' 
        },
        styles: { 
          fontSize: 8, 
          cellPadding: 3, 
          lineColor: [220, 220, 220], 
          lineWidth: 0.1 
        },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = pdf.lastAutoTable.finalY + 15;
      
      // Forecast Evolution Chart - Text description since we can't include the actual chart
      yPosition = await addSectionHeader('ÉVOLUTION DE LA DEMANDE', yPosition);
      
      // Add chart description
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      pdf.text("L'évolution de la demande est représentée graphiquement dans l'application.", 20, yPosition);
      pdf.text("Ce rapport PDF présente les données numériques correspondantes.", 20, yPosition + 4);
      yPosition += 15;
      
      // Add forecast data table
      const historicalData = forecastData.filter(item => item.isHistory);
      const forecastedData = forecastData.filter(item => item.isForecast);
      
      // Format the forecast data for the table
      const forecastTableData = forecastData.map(item => {
        if (item.isHistory) {
          return [
            item.month,
            'Historique',
            item.demand.toString(),
            '-',
            '-'
          ];
        } else {
          return [
            item.month,
            'Prévision',
            item.demand.expected.toString(),
            item.demand.lower.toString(),
            item.demand.upper.toString()
          ];
        }
      });
      
      autoTable(pdf, {
        startY: yPosition,
        head: [['Mois', 'Type', 'Demande', 'Min', 'Max']],
        body: forecastTableData,
        theme: 'plain',
        headStyles: { 
          fillColor: primaryRed, 
          textColor: 255, 
          fontSize: 8, 
          halign: 'left' 
        },
        styles: { 
          fontSize: 7, 
          cellPadding: 2, 
          lineColor: [230, 230, 230], 
          lineWidth: 0.1 
        },
        alternateRowStyles: { fillColor: lightGray },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = pdf.lastAutoTable.finalY + 15;
      
      // Products forecasts detail
      yPosition = await addSectionHeader('DÉTAILS PAR PRODUIT', yPosition);
      
      if (productForecasts && productForecasts.length > 0) {
        // Format product forecast data for the table
        const productTableData = productForecasts.slice(0, 15).map((product, index) => {
          const historicalAvg = product.stats.avgHistorical;
          const forecastAvg = product.stats.avgForecast;
          const growthRate = product.stats.growthRate;
          const reliability = product.stats.reliability;
          
          return [
            (index + 1).toString(),
            product.name,
            product.category,
            historicalAvg.toString(),
            forecastAvg.toString(),
            `${growthRate > 0 ? '+' : ''}${growthRate.toFixed(1)}%`,
            `${reliability}%`
          ];
        });
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['#', 'Produit', 'Catégorie', 'Hist. Moy.', 'Prév. Moy.', 'Évol.', 'Fiabilité']],
          body: productTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 8, 
            halign: 'left' 
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2, 
            lineColor: [230, 230, 230], 
            lineWidth: 0.1 
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      } else {
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.text('Aucune donnée de prévision par produit disponible.', 20, yPosition);
        yPosition += 10;
      }
      
      // Recommendations
      yPosition = await addSectionHeader('RECOMMANDATIONS', yPosition);
      
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(60, 60, 60);
      
      if (productStats.growthRate > 10) {
        pdf.text('Forte croissance prévue', 20, yPosition);
        yPosition += 5;
        pdf.setFont('helvetica', 'normal');
        pdf.text(`La demande devrait augmenter de ${productStats.growthRate}% dans les prochains mois.`, 20, yPosition);
        yPosition += 4;
        pdf.text('Augmentez vos niveaux de stock et préparez votre chaîne d\'approvisionnement en conséquence.', 20, yPosition);
        yPosition += 10;
      } else if (productStats.growthRate < -5) {
        pdf.text('Baisse de demande anticipée', 20, yPosition);
        yPosition += 5;
        pdf.setFont('helvetica', 'normal');
        pdf.text(`La demande devrait diminuer de ${Math.abs(productStats.growthRate)}% dans les prochains mois.`, 20, yPosition);
        yPosition += 4;
        pdf.text('Réduisez progressivement vos commandes et écoulez les stocks existants.', 20, yPosition);
        yPosition += 10;
      } else {
        pdf.text('Demande stable', 20, yPosition);
        yPosition += 5;
        pdf.setFont('helvetica', 'normal');
        pdf.text(`La demande devrait rester relativement stable (${productStats.growthRate > 0 ? '+' : ''}${productStats.growthRate}%).`, 20, yPosition);
        yPosition += 4;
        pdf.text('Maintenez vos niveaux actuels d\'approvisionnement avec des ajustements mineurs.', 20, yPosition);
        yPosition += 10;
      }
      
      if (productStats.reliability < 75) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Fiabilité limitée', 20, yPosition);
        yPosition += 5;
        pdf.setFont('helvetica', 'normal');
        pdf.text(`La fiabilité de cette prévision est de ${productStats.reliability}%.`, 20, yPosition);
        yPosition += 4;
        pdf.text('Considérez des scénarios alternatifs et maintenez une flexibilité dans votre planification.', 20, yPosition);
      }
      
      // Add header/footer to all pages
      const totalPages = pdf.getNumberOfPages();
      for (let i = 2; i <= totalPages; i++) {
        pdf.setPage(i);
        currentPage = i;
        await addHeaderFooter();
      }
      
      // Generate filename and download
      const fileName = `Prévisions_Demande_${new Date().toISOString().slice(0, 10)}.pdf`;
      console.log('💾 Saving PDF:', fileName);
      
      pdf.save(fileName);
      
      setIsGenerating(false);
      toast.success('Rapport PDF généré avec succès!');
      console.log('✅ PDF generation completed successfully');
      
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      setIsGenerating(false);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header with navigation */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/advanced-analytics')}
            className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Prévisions de Demande</h1>
            <p className="text-white/60 text-sm mt-1">
              Analyse et prévision basées sur l'historique des ventes
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button 
            onClick={handleGenerateReport}
            disabled={isGenerating}
            className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            <span>{isGenerating ? 'Génération...' : 'Exporter PDF'}</span>
          </button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-3">
            <FunnelIcon className="h-5 w-5 text-white/70" />
            <h3 className="text-lg font-medium text-white">Filtres</h3>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-white/70">Catégorie:</span>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-white/70">Produit:</span>
              <select
                value={productFilter}
                onChange={(e) => handleProductSelect(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {productOptions.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-white/70">Horizon:</span>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {timeRanges.map(range => (
                  <option key={range.id} value={range.id}>
                    {range.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-white/60 text-sm">Demande mensuelle moyenne</p>
              <p className="text-3xl font-bold text-white mt-1">{productStats.avgHistorical || 0}</p>
            </div>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700">
              <ChartBarIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-white/50">Basée sur les données historiques</p>
        </div>
        
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-white/60 text-sm">Demande future moyenne</p>
              <p className="text-3xl font-bold text-white mt-1">{productStats.avgForecast || 0}</p>
            </div>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-600 to-purple-700">
              <PresentationChartLineIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-white/50">Prévision sur {timeRange === '3months' ? '3' : timeRange === '6months' ? '6' : '12'} mois</p>
        </div>
        
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-white/60 text-sm">Taux de croissance</p>
              <p className={`text-3xl font-bold mt-1 ${productStats.growthRate > 0 ? 'text-green-400' : productStats.growthRate < 0 ? 'text-red-400' : 'text-white'}`}>
                {productStats.growthRate > 0 ? '+' : ''}{productStats.growthRate || 0}%
              </p>
            </div>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-700">
              <ArrowTrendingUpIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-white/50">Évolution prévue</p>
        </div>
        
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-white/60 text-sm">Fiabilité de la prévision</p>
              <p className="text-3xl font-bold text-white mt-1">{productStats.reliability || 0}%</p>
            </div>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-600 to-amber-700">
              <CalendarDaysIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-white/50">Niveau de confiance</p>
        </div>
      </div>
      
      {/* Forecast Chart */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-6">
          Prévision de demande pour {productFilter === 'all' ? 'tous les produits' : productOptions.find(p => p.id === productFilter)?.name}
        </h3>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="month" 
                stroke="rgba(255,255,255,0.6)"
                angle={-45}
                textAnchor="end"
                height={70}
              />
              <YAxis stroke="rgba(255,255,255,0.6)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: '#fff'
                }}
              />
              <Legend 
                verticalAlign="top" 
                height={36}
                formatter={(value, entry, index) => (
                  <span style={{ color: '#ffffff' }}>{value}</span>
                )}
              />
              <Bar dataKey="Historique" fill="#3B82F6" />
              <Bar dataKey="Prévision" fill="#8B5CF6" />
              <Line dataKey="Min" stroke="#10B981" strokeDasharray="3 3" dot={false} />
              <Line dataKey="Max" stroke="#EF4444" strokeDasharray="3 3" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Product forecast table */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-xl font-semibold text-white">Prévisions détaillées par produit</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Catégorie</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Demande historique</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Demande prévue</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Évolution</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Fiabilité</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filteredProducts.map((product, index) => {
                // Get real product forecast data
                const productForecast = productForecasts?.find(p => p.id === product.id);
                
                if (!productForecast) return null; // Skip if no forecast data
                
                const { stats } = productForecast;
                const historicalDemand = stats.avgHistorical || 0;
                const forecastDemand = stats.avgForecast || 0;
                const growth = stats.growthRate || 0;
                const reliability = stats.reliability || 70;
                
                return (
                  <tr key={product.id} className={`hover:bg-white/5 transition-colors ${productFilter === product.id ? 'bg-white/10' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button 
                        onClick={() => handleProductSelect(product.id)}
                        className="text-sm text-white hover:underline font-medium"
                      >
                        {product.name}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">
                      {categories.find(c => c.id === product.category)?.name || 'Non catégorisé'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white/80">{Math.round(historicalDemand)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white/80">{Math.round(forecastDemand)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <span className={growth >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-16 bg-white/10 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              reliability > 80 ? 'bg-green-500' : 
                              reliability > 70 ? 'bg-amber-500' : 
                              'bg-red-500'
                            }`}
                            style={{ width: `${reliability}%` }}
                          ></div>
                        </div>
                        <span className="ml-2 text-sm text-white/80">{reliability}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Recommendations section */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">Recommandations d'approvisionnement</h3>
        
        <div className="space-y-4">
          {productStats.growthRate > 10 && (
            <div className="bg-white/5 rounded-xl p-5">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <ArrowTrendingUpIcon className="h-5 w-5 text-green-400" />
                </div>
                <h4 className="text-white font-medium">Forte croissance prévue</h4>
              </div>
              <p className="text-white/70 text-sm">
                La demande devrait augmenter de {productStats.growthRate}% dans les prochains mois. 
                Augmentez vos niveaux de stock et préparez votre chaîne d'approvisionnement en conséquence.
              </p>
            </div>
          )}
          
          {productStats.growthRate < -5 && (
            <div className="bg-white/5 rounded-xl p-5">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <ArrowTrendingUpIcon className="h-5 w-5 text-red-400" transform="rotate(180)" />
                </div>
                <h4 className="text-white font-medium">Baisse de demande anticipée</h4>
              </div>
              <p className="text-white/70 text-sm">
                La demande devrait diminuer de {Math.abs(productStats.growthRate)}% dans les prochains mois. 
                Réduisez progressivement vos commandes et écoulez les stocks existants.
              </p>
            </div>
          )}
          
          {productStats.reliability < 75 && (
            <div className="bg-white/5 rounded-xl p-5">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <CalendarDaysIcon className="h-5 w-5 text-amber-400" />
                </div>
                <h4 className="text-white font-medium">Fiabilité limitée</h4>
              </div>
              <p className="text-white/70 text-sm">
                La fiabilité de cette prévision est de {productStats.reliability}%. Considérez des scénarios alternatifs
                et maintenez une flexibilité dans votre planification d'approvisionnement.
              </p>
            </div>
          )}
          
          {productStats.growthRate >= -5 && productStats.growthRate <= 10 && (
            <div className="bg-white/5 rounded-xl p-5">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <ChartBarIcon className="h-5 w-5 text-blue-400" />
                </div>
                <h4 className="text-white font-medium">Demande stable</h4>
              </div>
              <p className="text-white/70 text-sm">
                La demande devrait rester relativement stable ({productStats.growthRate > 0 ? '+' : ''}{productStats.growthRate}%). 
                Maintenez vos niveaux actuels d'approvisionnement avec des ajustements mineurs.
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default DemandForecastAnalysis; 